import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { 
  FaCrown, FaCalendarAlt, FaClock, FaHeart, FaRocket, 
  FaWhatsapp, FaCreditCard, FaUniversity, FaMobileAlt,
  FaCheckCircle, FaExclamationTriangle, FaGift, FaStar
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Número de WhatsApp para contacto
const WHATSAPP_NUMBER = '573195825712';

// Nota de activación
const ACTIVATION_NOTE = '(el pago se procesa automáticamente con Mercado Pago)';

// Métodos de pago disponibles - Solo Mercado Pago
const PAYMENT_METHODS = [
  { 
    id: 'mercadopago', 
    name: 'Mercado Pago', 
    icon: FaCreditCard, 
    color: 'from-blue-500 to-cyan-600',
    description: 'Tarjeta débito/crédito',
    instructions: 'Serás redirigido a la pasarela de pago segura de Mercado Pago',
    requiresManualValidation: false,
    isGateway: true
  }
];

// Componente de tarjeta de estado VIP para el Dashboard
export function VIPStatusCard({ vipStatus, onRenew }) {
  if (!vipStatus?.is_vip) return null;

  const daysRemaining = vipStatus.days_remaining || 0;
  const isExpired = vipStatus.is_expired;
  const isUrgent = daysRemaining <= 7 && !isExpired;
  const isWarning = daysRemaining <= 15 && daysRemaining > 7;

  const getStatusConfig = () => {
    if (isExpired) {
      return {
        gradient: 'from-red-500 to-orange-500',
        bgGradient: 'from-red-50 to-orange-50',
        borderColor: 'border-red-300',
        icon: FaExclamationTriangle,
        title: '¡Tu plan ha vencido!',
        subtitle: 'Renueva para seguir transformándote',
        buttonText: 'Renovar Ahora',
        buttonClass: 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 animate-pulse'
      };
    }
    if (isUrgent) {
      return {
        gradient: 'from-amber-500 to-orange-500',
        bgGradient: 'from-amber-50 to-orange-50',
        borderColor: 'border-amber-300',
        icon: FaClock,
        title: `¡Quedan ${daysRemaining} días!`,
        subtitle: 'Renueva pronto para no perder tu precio especial',
        buttonText: 'Renovar Plan',
        buttonClass: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
      };
    }
    if (isWarning) {
      return {
        gradient: 'from-yellow-400 to-amber-500',
        bgGradient: 'from-yellow-50 to-amber-50',
        borderColor: 'border-yellow-300',
        icon: FaCalendarAlt,
        title: `${daysRemaining} días restantes`,
        subtitle: 'Tu membresía VIP está activa',
        buttonText: 'Ver Renovación',
        buttonClass: 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600'
      };
    }
    return {
      gradient: 'from-emerald-400 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-300',
      icon: FaCrown,
      title: `${daysRemaining} días restantes`,
      subtitle: '¡Disfruta tu membresía VIP!',
      buttonText: 'Renovar',
      buttonClass: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
    };
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className={`overflow-hidden border-2 ${config.borderColor} bg-gradient-to-br ${config.bgGradient}`}>
        <CardContent className="p-0">
          <div className="flex items-stretch">
            {/* Lado izquierdo - Icono y estado */}
            <div className={`w-24 bg-gradient-to-br ${config.gradient} flex flex-col items-center justify-center p-4`}>
              <IconComponent className="text-white text-3xl mb-2" />
              <FaCrown className="text-yellow-300 text-xl" />
            </div>
            
            {/* Contenido principal */}
            <div className="flex-1 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white">
                      👑 VIP
                    </Badge>
                    <span className="text-sm font-medium text-gray-600 capitalize">
                      {(() => {
                        const pt = (vipStatus.plan_type || '').toLowerCase().replace(/^plan[_-]?/i, '');
                        const nameMap = { 'competitivo': 'Plan Competitivo', 'gold': 'Plan Gold VIP', 'premium': 'Plan Premium', 'basic': 'Fit Evolution', 'videos': 'Videos' };
                        return nameMap[pt] || `Plan ${pt.charAt(0).toUpperCase() + pt.slice(1)}`;
                      })()}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">{config.title}</h3>
                  <p className="text-sm text-gray-600">{config.subtitle}</p>
                </div>
                
                <div className="text-right">
                  {(vipStatus.fixed_price || vipStatus.fixed_price_usd) && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500">Tu precio especial</p>
                      <p className="text-xl font-bold text-green-600">
                        {vipStatus.fixed_price_usd && (!vipStatus.fixed_price || vipStatus.purchase_currency === 'USD')
                          ? `$${vipStatus.fixed_price_usd?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD`
                          : `$${vipStatus.fixed_price?.toLocaleString('es-CO')} COP`
                        }
                      </p>
                    </div>
                  )}
                  <Button 
                    onClick={onRenew}
                    className={`${config.buttonClass} text-white font-semibold`}
                  >
                    {config.buttonText}
                  </Button>
                </div>
              </div>
              
              {/* Barra de progreso */}
              {!isExpired && (
                <div className="mt-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full bg-gradient-to-r ${config.gradient}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, (daysRemaining / 30) * 100)}%` }}
                      transition={{ duration: 1, delay: 0.3 }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Modal de renovación/pago VIP
export function VIPRenewalModal({ isOpen, onClose, vipStatus, onPaymentComplete }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  // Selector de plan: 'monthly' o 'yearly' (con 40% off sobre el total anual)
  const [selectedPlanChoice, setSelectedPlanChoice] = useState('monthly');

  // Precio real del plan (para el anual, y de respaldo para el mensual) —
  // NUNCA se deriva el anual del mensual con un factor fijo: se lee
  // price_yearly_cop/price_yearly directo del plan, igual que PlanExpiredOverlay.
  const [planPricing, setPlanPricing] = useState(null);
  useEffect(() => {
    let alive = true;
    const loadPlanPricing = async () => {
      try {
        const res = await axios.get(`${API}/api/public/subscription-plans`);
        const plans = res.data?.plans || [];
        const target = String(vipStatus?.plan_type || 'basic').toLowerCase();
        const p = plans.find((pl) => pl.slug === target || pl.id === target || pl.id === `plan_${target}`);
        if (alive && p) {
          const pricing = p.pricing || {};
          setPlanPricing({
            monthly_cop: Number(p.price_monthly_cop || pricing.monthly?.cop || 0),
            monthly_usd: Number(p.price_monthly || pricing.monthly?.usd || 0),
            yearly_cop: Number(p.price_yearly_cop || pricing.yearly?.cop || 0),
            yearly_usd: Number(p.price_yearly || pricing.yearly?.usd || 0),
          });
        }
      } catch {
        // silent - se cae a los fallbacks de abajo
      }
    };
    loadPlanPricing();
    return () => { alive = false; };
  }, [vipStatus?.plan_type]);

  // Determinar moneda y precio base
  // Si el cliente tiene precio USD y compró en USD (o no tiene COP), usar USD
  const hasUSDPrice = vipStatus?.fixed_price_usd && vipStatus.fixed_price_usd > 0;
  const hasCOPPrice = vipStatus?.fixed_price && vipStatus.fixed_price > 0;
  const purchaseCurrency = vipStatus?.purchase_currency;

  // Lógica de moneda: priorizar la moneda en que compró el cliente
  const useUSD = (purchaseCurrency === 'USD' && hasUSDPrice) || (!hasCOPPrice && hasUSDPrice);
  const currency = useUSD ? 'USD' : 'COP';

  // El precio fijo VIP solo es un override válido para el periodo que el admin
  // realmente configuró en vip_clients (use_fixed_price=true + subscription_period).
  // Usamos vip_raw_fixed_price/_usd (el valor crudo de vip_clients.fixed_price, SIN
  // los fallbacks genéricos como profile.subscription_price) para decidir si existe
  // un override real - de lo contrario cualquier fallback terminaba haciéndose pasar
  // por "precio fijo VIP" y contaminando el cálculo del periodo que no corresponde
  // (ese era el bug: el mensual/anual se derivaba de un valor que ni siquiera era
  // un precio especial del cliente).
  const vipOverridePeriod = String(vipStatus?.subscription_period || 'monthly').toLowerCase();
  const vipOverrideIsYearly = ['yearly', 'annual', 'anual', 'año', 'ano'].includes(vipOverridePeriod);
  const hasVipOverride = !!vipStatus?.use_fixed_price;
  const hasRawVipCOPOverride = vipStatus?.vip_raw_fixed_price > 0;
  const hasRawVipUSDOverride = vipStatus?.vip_raw_fixed_price_usd > 0;

  // Precio MENSUAL: precio fijo VIP solo si el override es para el periodo mensual;
  // si no, el precio real del plan.
  const monthlyPriceCOP = (hasVipOverride && !vipOverrideIsYearly && hasRawVipCOPOverride)
    ? vipStatus.vip_raw_fixed_price
    : (planPricing?.monthly_cop || 125000);
  const monthlyPriceUSD = (hasVipOverride && !vipOverrideIsYearly && hasRawVipUSDOverride)
    ? vipStatus.vip_raw_fixed_price_usd
    : (planPricing?.monthly_usd || null);
  const monthlyPrice = useUSD ? monthlyPriceUSD : monthlyPriceCOP;

  // When the tile above holds a non-monthly special price, label it with that period.
  const tileShowsVipSpecial = hasVipOverride && !vipOverrideIsYearly && (useUSD ? hasRawVipUSDOverride : hasRawVipCOPOverride);
  const firstTileLabel = (tileShowsVipSpecial && {
    bimonthly: { title: 'Bimestral', subtitle: 'Renovación cada 2 meses' },
    quarterly: { title: 'Trimestral', subtitle: 'Renovación cada 3 meses' },
    semester: { title: 'Semestral', subtitle: 'Renovación cada 6 meses' },
    semiannual: { title: 'Semestral', subtitle: 'Renovación cada 6 meses' },
  }[vipOverridePeriod]) || { title: 'Mensual', subtitle: 'Renovación cada mes' };

  // Precio ANUAL: precio fijo VIP solo si el override es para el periodo anual;
  // si no, price_yearly_cop/price_yearly real del plan. Solo si el plan tampoco
  // tiene un precio anual configurado, se estima con 40% off sobre 12 mensualidades.
  // A bimonthly/quarterly/semester special is not a monthly price, so the annual reference uses the catalog monthly.
  const isMultiMonthSpecial = tileShowsVipSpecial && ['bimonthly', 'quarterly', 'semester', 'semiannual'].includes(vipOverridePeriod);
  const annualMonthlyBase = isMultiMonthSpecial
    ? (useUSD ? (planPricing?.monthly_usd || null) : (planPricing?.monthly_cop || 125000))
    : monthlyPrice;
  const yearlyFullPrice = (annualMonthlyBase || 0) * 12; // referencia "sin descuento" para mostrar el ahorro
  const planYearlyPrice = useUSD ? planPricing?.yearly_usd : planPricing?.yearly_cop;
  const yearlyDiscountedPrice = (hasVipOverride && vipOverrideIsYearly && (useUSD ? hasRawVipUSDOverride : hasRawVipCOPOverride))
    ? (useUSD ? vipStatus.vip_raw_fixed_price_usd : vipStatus.vip_raw_fixed_price)
    : (planYearlyPrice || yearlyFullPrice * 0.60);
  const yearlySavings = Math.max(0, yearlyFullPrice - yearlyDiscountedPrice);

  // Precio base según elección del usuario
  const basePrice = selectedPlanChoice === 'yearly' ? yearlyDiscountedPrice : monthlyPrice;

  // Precio final: con descuento si aplica, si no el precio base
  const finalPrice = discountedPrice !== null ? discountedPrice : basePrice;
  const price = useUSD ? Math.max(parseFloat(finalPrice.toFixed(2)), 1) : Math.max(Math.round(finalPrice), 1000);

  // Período que se enviará al backend
  const selectedPeriod = selectedPlanChoice === 'yearly' ? 'yearly' : (vipStatus?.subscription_period || 'monthly');
  const periodLabel = {
    'monthly': 'Mensual',
    'bimonthly': 'Bimestral',
    'quarterly': 'Trimestral',
    'semester': 'Semestral',
    'yearly': 'Anual',
    'annual': 'Anual'
  }[selectedPeriod] || 'Mensual';

  // Reset código de descuento si cambia el plan (para forzar re-validación de precio)
  const handlePlanChoice = (choice) => {
    if (choice === selectedPlanChoice) return;
    setSelectedPlanChoice(choice);
    setPromoApplied(null);
    setDiscountedPrice(null);
    setPromoCode('');
  };

  // Formateador de moneda
  const fmt = (v) => useUSD
    ? `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
    : `$${Math.round(v || 0).toLocaleString('es-CO')} COP`;

  // Aplicar código de descuento
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    try {
      const response = await axios.post(`${API}/api/validate-promo-code`, {
        code: promoCode.trim(),
        item_type: 'subscription',
        price: basePrice,
        currency: currency
      });
      if (response.data.valid) {
        setPromoApplied({
          ...response.data,
          discount_display: response.data.message || `$${response.data.discount_amount?.toLocaleString('es-CO')} descuento`
        });
        setDiscountedPrice(response.data.final_price);
        toast.success(`¡Código aplicado! ${response.data.message}`);
      } else {
        toast.error(response.data.message || 'Código inválido');
        setPromoApplied(null);
        setDiscountedPrice(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al validar el código';
      toast.error(errorMsg);
      setPromoApplied(null);
      setDiscountedPrice(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleSelectMethod = async (method) => {
    setSelectedMethod(method);
    
    // Si es una pasarela de pago automática (Mercado Pago), procesar el pago
    if (method.isGateway) {
      setProcessingPayment(true);
      try {
        let endpoint;
        let amountToSend;
        
        if (method.id === 'mercadopago') {
          endpoint = '/api/payments/mercadopago/create';
          amountToSend = price; // En la moneda correcta (COP o USD, ya con descuento si aplica)
        } else {
          toast.error('Método de pago no disponible');
          setProcessingPayment(false);
          return;
        }
        
        // CRÍTICO: plan_type DEBE coincidir con el plan del usuario
        // vipStatus.plan_type viene del Dashboard con el plan correcto (basic, premium, gold, etc.)
        // NUNCA usar 'premium' como fallback - si no hay plan_type, usar 'basic' (el más restrictivo)
        const actualPlanType = vipStatus?.plan_type || 'basic';
        
        const token = localStorage.getItem('deisycfit_token');
        const response = await axios.post(`${API}${endpoint}`, {
          plan_id: `vip-renewal-${actualPlanType}`,
          plan_type: actualPlanType,
          plan_name: `Renovación - Plan ${actualPlanType.charAt(0).toUpperCase() + actualPlanType.slice(1)}`,
          subscription_period: selectedPeriod,
          product_type: 'subscription',
          payment_method: method.id,
          amount: amountToSend,
          currency_id: currency,
          title: `Renovación VIP - ${periodLabel}`,
          guest_email: vipStatus?.email,
          promo_code_id: promoApplied?.promo_code_id || null  // Enviar código de descuento si aplica
        }, { 
          withCredentials: false,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (response.data.success) {
          // Redirigir a la pasarela de pago
          const redirectUrl = response.data.redirect_url || response.data.payment_link || response.data.init_point || response.data.approval_url;
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            toast.error('No se pudo obtener el enlace de pago');
          }
        } else {
          toast.error(response.data.error || 'Error al crear el pago');
        }
      } catch (error) {
        console.error('Payment creation error:', error);
        const errorDetail = error.response?.data?.detail || error.response?.data?.error || error.message;
        toast.error(`Error al procesar el pago: ${errorDetail}`);
      } finally {
        setProcessingPayment(false);
      }
    } else {
      // Métodos manuales - mostrar instrucciones
      setShowInstructions(true);
    }
  };

  const handlePaymentSent = async () => {
    setPaymentSent(true);
    // Enviar notificación al admin
    try {
      await axios.post(`${API}/api/vip-payment-notification`, {
        email: vipStatus?.email,
        name: vipStatus?.name,
        amount: price,
        method: selectedMethod?.name,
        period: periodLabel
      }, { withCredentials: false });
      toast.success('¡Notificación enviada! Verificaremos tu pago pronto.');
    } catch (error) {
      console.error('Error sending payment notification:', error);
      toast.error('Error al enviar notificación, pero igual valida tu pago por WhatsApp');
    }
  };

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent(
      `¡Hola Deisy! Soy ${vipStatus?.name || 'cliente VIP'}. ` +
      `Acabo de realizar un pago de $${price?.toLocaleString('es-CO')} COP ` +
      `vía ${selectedMethod?.name || 'transferencia'} para renovar mi plan ${(() => {
        const pt = (vipStatus?.plan_type || 'VIP').toLowerCase().replace(/^plan[_-]?/i, '');
        const nameMap = { 'competitivo': 'COMPETITIVO', 'gold': 'GOLD VIP', 'premium': 'PREMIUM', 'basic': 'FIT EVOLUTION' };
        return nameMap[pt] || pt.toUpperCase() || 'VIP';
      })()} (${periodLabel}). ` +
      `Mi correo es: ${vipStatus?.email}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const resetModal = () => {
    setSelectedMethod(null);
    setShowInstructions(false);
    setPaymentSent(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" style={{ backgroundColor: '#111111', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
        <AnimatePresence mode="wait">
          {!showInstructions && !paymentSent && (
            <motion.div
              key="methods"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <DialogHeader className="text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <FaCrown className="text-yellow-300 text-4xl" />
                </div>
                <DialogTitle className="text-2xl font-bold text-white">
                  {vipStatus?.is_expired ? '¡Renueva tu Membresía!' : 'Renovar Membresía'}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  {vipStatus?.is_expired 
                    ? '¡Te extrañamos! Vuelve a disfrutar de todos los beneficios'
                    : 'Mantén tu precio especial renovando tu plan'
                  }
                </DialogDescription>
              </DialogHeader>

              <div className="py-4">
                {/* ═══════ SELECTOR DE PLAN: MENSUAL vs ANUAL (40% OFF) ═══════ */}
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-300 mb-2">Elige tu plan:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Opción Mensual */}
                    <button
                      onClick={() => handlePlanChoice('monthly')}
                      data-testid="renewal-plan-monthly"
                      className="relative rounded-xl p-3 text-left transition-all"
                      style={{
                        border: selectedPlanChoice === 'monthly'
                          ? '2px solid #ec4899'
                          : '1px solid rgba(255,255,255,0.15)',
                        backgroundColor: selectedPlanChoice === 'monthly'
                          ? 'rgba(var(--brand-primary-glow), 0.15)'
                          : 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <p className="text-xs uppercase tracking-widest font-bold" style={{ color: selectedPlanChoice === 'monthly' ? '#f472b6' : '#9ca3af' }}>
                        {firstTileLabel.title}
                      </p>
                      <p className="text-white text-lg font-black mt-1">
                        {fmt(monthlyPrice)}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{firstTileLabel.subtitle}</p>
                    </button>

                    {/* Opción Anual con 40% off */}
                    <button
                      onClick={() => handlePlanChoice('yearly')}
                      data-testid="renewal-plan-yearly"
                      className="relative rounded-xl p-3 text-left transition-all overflow-hidden"
                      style={{
                        border: selectedPlanChoice === 'yearly'
                          ? '2px solid #22d3ee'
                          : '1px solid rgba(255,255,255,0.15)',
                        backgroundColor: selectedPlanChoice === 'yearly'
                          ? 'rgba(34, 211, 238, 0.15)'
                          : 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <span
                        className="absolute top-1.5 right-1.5 text-[9px] font-black px-1.5 py-0.5 rounded-full"
                        style={{ background: 'linear-gradient(90deg,#facc15,#f97316)', color: '#0a0a0a' }}
                      >
                        -40% OFF
                      </span>
                      <p className="text-xs uppercase tracking-widest font-bold" style={{ color: selectedPlanChoice === 'yearly' ? '#67e8f9' : '#9ca3af' }}>
                        Anual
                      </p>
                      <p className="text-white text-lg font-black mt-1">
                        {fmt(yearlyDiscountedPrice)}
                      </p>
                      <p className="text-[11px] text-gray-500 line-through">
                        {fmt(yearlyFullPrice)}
                      </p>
                      <p className="text-[10px] text-emerald-400 font-bold mt-0.5">
                        Ahorras {fmt(yearlySavings)}
                      </p>
                    </button>
                  </div>
                  {selectedPlanChoice === 'yearly' && (
                    <p className="text-[11px] text-cyan-300 mt-2 italic">
                      🎉 Un solo pago por 12 meses. Bloquea tu precio y ahórrate el 40%.
                    </p>
                  )}
                </div>

                {/* Resumen del plan */}
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: 'rgba(168, 85, 247, 0.15)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-400">
                        {(() => {
                          const pt = (vipStatus?.plan_type || '').toLowerCase().replace(/^plan[_-]?/i, '');
                          const nameMap = { 'competitivo': 'Competitivo', 'gold': 'Gold VIP', 'premium': 'Premium', 'basic': 'Fit Evolution', 'videos': 'Videos' };
                          return `Plan ${nameMap[pt] || pt.charAt(0).toUpperCase() + pt.slice(1) || 'VIP'}`;
                        })()}
                      </p>
                      <p className="font-semibold text-white">{periodLabel}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Precio a pagar</p>
                      <p className="text-2xl font-bold text-green-400">
                        ${useUSD ? price?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : price?.toLocaleString('es-CO')} {currency}
                      </p>
                      {promoApplied && (
                        <p className="text-xs text-gray-500 line-through">
                          ${useUSD ? basePrice?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : basePrice?.toLocaleString('es-CO')} {currency}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-purple-300 rounded-lg p-2" style={{ backgroundColor: 'rgba(168, 85, 247, 0.2)' }}>
                    <FaStar className="text-yellow-400" />
                    <span>Precio exclusivo para ti</span>
                  </div>
                </div>

                {/* Campo de código de descuento */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-300 mb-2">¿Tienes un código de descuento?</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Ingresa tu código"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      disabled={applyingPromo || promoApplied}
                      className="flex-1 px-3 py-2 rounded-lg text-white placeholder:text-gray-500 disabled:opacity-50"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                    />
                    {promoApplied ? (
                      <button
                        onClick={() => { setPromoApplied(null); setDiscountedPrice(null); setPromoCode(''); }}
                        className="px-4 py-2 rounded-lg text-red-400 font-medium"
                        style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                      >
                        Quitar
                      </button>
                    ) : (
                      <button
                        onClick={handleApplyPromo}
                        disabled={applyingPromo || !promoCode.trim()}
                        className="px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)' }}
                      >
                        {applyingPromo ? '...' : 'Aplicar'}
                      </button>
                    )}
                  </div>
                  {promoApplied && (
                    <p className="mt-2 text-sm text-green-400">✓ {promoApplied.discount_display} aplicado</p>
                  )}
                </div>

                {/* Métodos de pago */}
                <p className="text-sm font-medium text-gray-300 mb-3">Selecciona tu método de pago:</p>
                <div className="space-y-2">
                  {PAYMENT_METHODS.map((method) => {
                    const IconComponent = method.icon;
                    return (
                      <motion.button
                        key={method.id}
                        whileHover={{ scale: processingPayment ? 1 : 1.02 }}
                        whileTap={{ scale: processingPayment ? 1 : 0.98 }}
                        onClick={() => !processingPayment && handleSelectMethod(method)}
                        disabled={processingPayment}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all
                          ${processingPayment ? 'opacity-50 cursor-not-allowed' : 'hover:border-purple-400'}`}
                        style={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderColor: 'rgba(255, 255, 255, 0.1)' 
                        }}
                      >
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${method.color} flex items-center justify-center`}>
                          <IconComponent className="text-white text-xl" />
                        </div>
                        <div className="text-left flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white">{method.name}</p>
                            {method.isGateway && (
                              <Badge className="text-xs" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }}>Automático</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{method.description}</p>
                        </div>
                        {processingPayment ? (
                          <div className="animate-spin w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full" />
                        ) : (
                          <FaRocket className="text-purple-400" />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
                
                {processingPayment && (
                  <div className="mt-4 p-3 rounded-lg text-center" style={{ backgroundColor: 'rgba(168, 85, 247, 0.2)', border: '1px solid rgba(168, 85, 247, 0.4)' }}>
                    <p className="text-sm text-purple-300">
                      🔄 Redirigiendo a la pasarela de pago...
                    </p>
                  </div>
                )}
              </div>

              <div className="text-center text-sm text-gray-400">
                <button 
                  onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}`, '_blank')}
                  className="inline-flex items-center gap-1 hover:text-green-400 transition-colors"
                >
                  <FaWhatsapp className="text-green-500" />
                  ¿Dudas? Escríbenos por WhatsApp
                </button>
              </div>
            </motion.div>
          )}

          {showInstructions && !paymentSent && selectedMethod && (
            <motion.div
              key="instructions"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <DialogHeader className="text-center">
                <div className={`mx-auto w-16 h-16 bg-gradient-to-r ${selectedMethod.color} rounded-full flex items-center justify-center mb-4`}>
                  <selectedMethod.icon className="text-white text-2xl" />
                </div>
                <DialogTitle className="text-xl font-bold text-gray-800">
                  Pagar con {selectedMethod.name}
                </DialogTitle>
              </DialogHeader>

              <div className="py-4 space-y-4">
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4">
                  <p className="text-center text-sm text-gray-600 mb-2">Monto a pagar:</p>
                  <p className="text-center text-3xl font-bold text-purple-700">
                    ${price?.toLocaleString('es-CO')} COP
                  </p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">📋 Instrucciones:</p>
                  <p className="text-gray-600">{selectedMethod.instructions}</p>
                  
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-700">
                      <strong>💡 Importante:</strong> Una vez realizado el pago, envía el comprobante por WhatsApp para activar tu plan más rápido.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={handleWhatsAppContact}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                  >
                    <FaWhatsapp className="mr-2" /> Enviar comprobante por WhatsApp
                  </Button>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowInstructions(false)}
                      className="flex-1"
                    >
                      ← Cambiar método
                    </Button>
                    <Button 
                      onClick={handlePaymentSent}
                      className={`flex-1 bg-gradient-to-r ${selectedMethod.color} hover:opacity-90 text-white`}
                    >
                      ✓ Ya envié el pago
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {paymentSent && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="mx-auto w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg"
                >
                  <FaCheckCircle className="text-white text-5xl" />
                </motion.div>
                
                <h3 className="text-2xl font-bold text-gray-800 mb-2">¡Gracias por tu pago! 💕</h3>
                <p className="text-gray-600 mb-6">
                  Hemos recibido tu notificación de pago.
                  <br />
                  <span className="text-purple-600 font-medium">
                    Verificaremos el pago y activaremos tu plan en las próximas horas.
                  </span>
                </p>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-600">
                    <FaWhatsapp className="inline mr-2 text-green-500" />
                    Si tienes alguna duda, no dudes en escribirnos por WhatsApp.
                  </p>
                </div>

                <Button 
                  onClick={handleClose}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8"
                >
                  <FaHeart className="mr-2" /> ¡Entendido!
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// Componente combinado para exportar
export default function VIPSubscription({ vipStatus, onStatusUpdate }) {
  const [showRenewalModal, setShowRenewalModal] = useState(false);

  // Mostrar automáticamente si el plan está vencido
  useEffect(() => {
    if (vipStatus?.is_expired && !sessionStorage.getItem('renewal_modal_shown')) {
      const timer = setTimeout(() => {
        setShowRenewalModal(true);
        sessionStorage.setItem('renewal_modal_shown', 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [vipStatus?.is_expired]);

  return (
    <>
      <VIPStatusCard 
        vipStatus={vipStatus} 
        onRenew={() => setShowRenewalModal(true)} 
      />
      <VIPRenewalModal
        isOpen={showRenewalModal}
        onClose={() => setShowRenewalModal(false)}
        vipStatus={vipStatus}
        onPaymentComplete={onStatusUpdate}
      />
    </>
  );
}
